import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import toast from "react-hot-toast";
import { useMarkOnboardingSeenMutation } from "../../Services/api.user.slice";
import { DASH } from "../../Pages/PropertyDetailPage/dashboard/tokens";
import { ApnOnboardingData, OnboardingVariant } from "./onboardingTypes";
import { ackPending, ackRejected, isPendingAcked, isRejectedAcked } from "./onboardingStorage";

interface TriggerParams {
  /** REAPI property id of the home this page is showing. */
  propertyId: string;
  isAuthenticated: boolean;
  isOwner: boolean;
  /** Short address line for the toast / modal copy. */
  address?: string;
  /** apn-status payload (apnStatusData?.data). */
  apn: ApnOnboardingData | null | undefined;
}

/**
 * Drives the onboarding pop-up on the property dashboard. The decision is
 * anchored on the home's STATE (not a login event), re-evaluated whenever
 * apn-status loads or refreshes — so it fires on a fresh visit, on a revisit
 * while already logged in, and when the state flips while the user is sitting
 * on the page. A fresh arrival opens the pop-up directly; a passive flip
 * (admin approves/rejects while they're here) nudges with a toast instead of
 * hijacking the screen. A claim the user just submitted (PENDING) always opens
 * directly — it's their own action.
 */
export function useOnboardingTrigger({
  propertyId,
  isAuthenticated,
  isOwner,
  address,
  apn,
}: TriggerParams): { variant: OnboardingVariant | null; close: () => void } {
  const [variant, setVariant] = useState<OnboardingVariant | null>(null);
  const [markSeen] = useMarkOnboardingSeenMutation();

  const status = apn?.status ?? null;
  const apnId = apn?.apnId ?? null;
  const createdAt = apn?.createdAt ?? null;
  const statusUpdatedAt = apn?.statusUpdatedAt ?? null;
  const onboardingSeenAt = apn?.onboardingSeenAt ?? null;
  const home = address || "Your home";

  // The last DECIDED status (APPROVED/REJECTED/PENDING) we acted on — undefined
  // until the first one, and never touched by a not-yet-loaded null run. apn-
  // status and isOwner arrive across several renders, so keying "fresh arrival
  // vs flip" off decided statuses (not off whether any effect has run) is what
  // keeps the welcome pop-up from degrading to a toast on a normal visit.
  const prevDecidedRef = useRef<string | null | undefined>(undefined);
  const variantRef = useRef<OnboardingVariant | null>(null);
  const dismissedApprovedRef = useRef(false);
  variantRef.current = variant;

  const close = useCallback(() => {
    const v = variantRef.current;
    if (v === "approved") {
      // Server truth so the tour never shows again for this home, on any device.
      dismissedApprovedRef.current = true; // guard the gap until the refetch lands
      markSeen({ propertyId });
    } else if (v === "rejected") {
      ackRejected(apnId, statusUpdatedAt);
    } else if (v === "pending") {
      ackPending(propertyId, createdAt);
    }
    setVariant(null);
  }, [markSeen, propertyId, apnId, statusUpdatedAt, createdAt]);

  const showLiveToast = useCallback(
    (v: "approved" | "rejected") => {
      const open = () => setVariant(v);
      const label =
        v === "approved" ? (
          <span>
            🎉 <b>{home}</b> is verified — <b>Take the tour</b>
          </span>
        ) : (
          <span>
            We need a bit more on <b>{home}</b> — <b>See details</b>
          </span>
        );
      toast(
        (t) => (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => {
              toast.dismiss(t.id);
              open();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                toast.dismiss(t.id);
                open();
              }
            }}
            sx={{ cursor: "pointer", fontSize: 14, color: "#1d2b2b", outline: "none" }}
          >
            {label}
          </Box>
        ),
        {
          duration: 9000,
          icon: v === "approved" ? "✅" : "📄",
          style: { borderLeft: `4px solid ${v === "approved" ? DASH.green : DASH.amber}` },
        }
      );
    },
    [home]
  );

  useEffect(() => {
    if (!isAuthenticated || !propertyId) return;

    // Which pop-up *should* be showing for the current state, gated so each
    // fires once (server flag for approved, per-viewer acks for the others).
    let next: OnboardingVariant | null = null;
    if (status === "APPROVED" && isOwner && !onboardingSeenAt && !dismissedApprovedRef.current) {
      next = "approved";
    } else if (status === "REJECTED" && !isRejectedAcked(apnId, statusUpdatedAt)) {
      next = "rejected";
    } else if (status === "PENDING" && !isPendingAcked(propertyId, createdAt)) {
      next = "pending";
    }

    // Compare against the last DECIDED status, and record only decided ones, so
    // the not-yet-loaded null renders (and an isOwner that catches up a render
    // later) don't register as a change.
    const decidedPrev = prevDecidedRef.current;
    if (status) prevDecidedRef.current = status;

    // Let a future re-approval show again once the server agrees it's seen, or
    // once the home leaves the approved state.
    if (status !== "APPROVED" || onboardingSeenAt) dismissedApprovedRef.current = false;

    if (!next || variantRef.current) return;

    // A real flip is a change BETWEEN two decided statuses (e.g. an admin
    // approves while the user is sitting here) — nudge with a toast rather than
    // hijack. Everything else is a fresh arrival (the first decided status, or
    // the same one finishing loading across renders) — open the pop-up. A
    // just-submitted PENDING always opens: it's the user's own action.
    const liveFlip =
      decidedPrev !== undefined && decidedPrev !== null && decidedPrev !== status;

    if (next === "pending" || !liveFlip) {
      setVariant(next);
    } else {
      showLiveToast(next as "approved" | "rejected");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, onboardingSeenAt, isOwner, isAuthenticated, propertyId, apnId, statusUpdatedAt, createdAt]);

  return { variant, close };
}
